import { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { usePortalPosition } from "../usePortalPosition";
import { PORTAL_ROOT_ID } from "../constants";
import { lockBodyScroll, unlockBodyScroll } from "../useScrollLock";
import styles from "./style.module.css";

const VARIANT_CLASS = {
  default: styles.dropdownDefault,
  category: styles.dropdownCategory,
};

/**
 * A dropdown panel rendered into document.body via a portal, anchored to a trigger element.
 * Never clips inside scrollable containers or modals.
 *
 * @param {React.RefObject} anchorRef - Ref on the element to anchor the dropdown below.
 * @param {boolean} open - Whether the dropdown is visible.
 * @param {Function} onClose - Called when the user clicks outside.
 * @param {React.ReactNode} children - Dropdown content.
 * @param {string} [className] - Extra class for the dropdown panel.
 * @param {keyof typeof VARIANT_CLASS} [variant="default"] - Visual variant. Add new variants by extending VARIANT_CLASS and adding matching CSS.
 * @param {object} [positionOptions] - Overrides forwarded to usePortalPosition.
 * @param {{value: string, onChange: Function, placeholder?: string}} [search] - Opt-in
 *   search box pinned above the options. Omit it and this component renders exactly as
 *   it always has — every existing caller is unaffected.
 *
 *   The panel stays headless: this only draws the input and pins it. Filtering is the
 *   caller's, because the caller owns the rows.
 */
const Dropdown = ({
  anchorRef,
  open,
  onClose,
  children,
  className,
  variant = "default",
  positionOptions = {},
  search = null,
}) => {
  const dropdownRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);
  // Callers pass onClose as a fresh inline arrow, and typing in the search box
  // re-renders them — held in a ref so the open-effects below run once per open
  // instead of redoing the ancestor freeze walk on every keystroke.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const { portalStyle } = usePortalPosition(anchorRect, dropdownRef, "bottom", {
    align: "left",
    matchAnchorWidth: true,
    offset: 4,
    ...positionOptions,
  });

  useEffect(() => {
    if (open && anchorRef.current) {
      setAnchorRect(anchorRef.current.getBoundingClientRect());
    } else {
      setAnchorRect(null);
    }
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;

    const handleEsc = (event) => {
      if (event.key === "Escape") {
        // Capture phase + stopPropagation: while a panel is open, Escape
        // dismisses just the panel. Without this, UI/Popup's own bubble-phase
        // Escape listener also fired and closed the whole modal underneath,
        // discarding whatever was typed into it.
        event.stopPropagation();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", handleEsc, true);
    return () => document.removeEventListener("keydown", handleEsc, true);
  }, [open]);

  // The panel is position:fixed against a rect measured when it opened, so
  // nothing may move underneath it. `useScrollLock` pins the body; every
  // scrollable ancestor of the anchor is frozen too — a hardcoded lookup of
  // the Popup shell missed pinned-header modals, whose scroller is an inner
  // body, and the open panel detached from its trigger on scroll.
  //
  // Deliberately its own effect with DOM-only deps: the walk does forced
  // layout reads, and must run once per open, not once per parent render.
  useEffect(() => {
    if (!open) return;

    const frozen = [];
    let node = anchorRef?.current?.parentElement;
    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const canScroll =
        /(auto|scroll)/.test(`${style.overflowY}${style.overflowX}`) &&
        (node.scrollHeight > node.clientHeight ||
          node.scrollWidth > node.clientWidth);
      if (canScroll) {
        frozen.push([node, node.style.overflow]);
        node.style.overflow = "hidden";
      }
      node = node.parentElement;
    }

    lockBodyScroll();
    return () => {
      unlockBodyScroll();
      frozen.forEach(([element, previous]) => {
        element.style.overflow = previous;
      });
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onCloseRef.current?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, anchorRef]);

  if (!open || !anchorRect) return null;

  const variantClass = VARIANT_CLASS[variant] || VARIANT_CLASS.default;

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className={`${styles.dropdown} ${variantClass} ${
        search ? styles.dropdownSearchable : ""
      } ${className || ""}`}
      style={portalStyle}
    >
      {search ? (
        <>
          {/* Scrolling moves to the list below, so the input stays put while
              the options move under it. */}
          <div className={styles.searchRow}>
            <input
              className={styles.searchInput}
              type="text"
              value={search.value ?? ""}
              placeholder={search.placeholder || "Search"}
              autoFocus
              onChange={(event) => search.onChange?.(event.target.value)}
              // Deliberately no keydown handler: the Escape-to-close listener is
              // on `document`, and stopping propagation here would swallow it
              // whenever focus sat in this box.
            />
          </div>
          <div className={styles.searchBody}>{children}</div>
        </>
      ) : (
        children
      )}
    </div>,
    document.getElementById(PORTAL_ROOT_ID) || document.body,
  );
};

export default Dropdown;
